import { OrganizationAccessLevel } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/organization_access_pb";
import type { OrganizationMember } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/organization_pb";
import { OrganizationService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/organization_connect";
import { UserService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/user_connect";
import { createConnectClient } from "server/connect";

export interface OrganizationMemberAccess {
  userId: string;
  displayName: string;
  defaultEmail: string;
  organizationId: string;
  accessLevel: OrganizationAccessLevel;
}

interface OrganizationClient {
  listOrganizationMembers: (request: {
    organizationId: string;
  }) => Promise<{ items: OrganizationMember[] }>;
}

interface UserClient {
  putOrganizationAccessForUser: (request: {
    organizationId: string;
    userId: string;
    accessLevel: OrganizationAccessLevel;
  }) => Promise<unknown>;
  removeOrganizationAccessForUser: (request: {
    organizationId: string;
    userId: string;
  }) => Promise<unknown>;
}

interface OrganizationAccessClientDeps {
  organization?: Partial<OrganizationClient>;
  user?: Partial<UserClient>;
}

function mapOrganizationMember(
  member: OrganizationMember,
): OrganizationMemberAccess {
  return {
    userId: member.userId,
    displayName: member.displayName,
    defaultEmail: member.defaultEmail,
    organizationId: member.access?.organizationId || "",
    accessLevel:
      member.access?.accessLevel || OrganizationAccessLevel.UNSPECIFIED,
  };
}

export function createOrganizationAccessClient(
  deps: OrganizationAccessClientDeps = {},
) {
  const defaultOrganizationClient = createConnectClient(OrganizationService);
  const defaultUserClient = createConnectClient(UserService);
  const organization = {
    ...defaultOrganizationClient,
    ...deps.organization,
  };
  const user = {
    ...defaultUserClient,
    ...deps.user,
  };

  return {
    listMembers: async (organizationId: string) => {
      const res = await organization.listOrganizationMembers({
        organizationId,
      });
      return {
        members: res.items.map(mapOrganizationMember),
      };
    },
    setAccess: (
      organizationId: string,
      userId: string,
      accessLevel: OrganizationAccessLevel,
    ) =>
      user.putOrganizationAccessForUser({
        organizationId,
        userId,
        accessLevel,
      }),
    removeAccess: (organizationId: string, userId: string) =>
      user.removeOrganizationAccessForUser({
        organizationId,
        userId,
      }),
  };
}

export const organizationAccessClient = createOrganizationAccessClient();
